import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/*
 * CRM, leads kanban as the centerpiece, with a stat strip above and a
 * single side-by-side "origem" mini-card row below. No 1+3 card pattern.
 *
 * Layout is shared between the Portuguese and English deep-dive pages; only
 * the copy, the sample names and the currency differ, so both live in
 * `CONTENT` below rather than in a forked component. (The *homepage* sections
 * do fork — there the English argument is structurally different.)
 */

type Card = { name: string; origin: string; value: string; when: string }
type Column = { cards: readonly Card[]; title: string; tone: string }
type Stat = { label: string; tone: string; value: string }
type Origin = { count: number; label: string; share: number; tone: string }

type CrmContent = {
  eyebrow: string
  heading: string
  intro: string
  mockupTitle: string
  trendBadge: string
  filterLabel: string
  newLeadLabel: string
  columns: readonly Column[]
  stats: readonly Stat[]
  originsCaption: string
  originsPeriod: string
  originsTotal: string
  origins: readonly Origin[]
}

const CONTENT: Record<MarketingLocale, CrmContent> = {
  en: {
    columns: [
      {
        cards: [
          { name: 'Marion Vaughan', origin: 'Website brief', value: '~$80k', when: 'today' },
          { name: 'Peter Sallow', origin: 'Referral', value: '~$45k', when: 'yesterday' },
        ],
        title: 'New lead',
        tone: '#5b8def',
      },
      {
        cards: [{ name: 'Carmine Studio', origin: 'Instagram', value: '~$32k', when: '3 days' }],
        title: 'In contact',
        tone: '#e8a13d',
      },
      {
        cards: [{ name: 'The Suttons', origin: 'Repeat client', value: '$86k', when: '5 days' }],
        title: 'Proposal sent',
        tone: '#7a6cb6',
      },
      {
        cards: [{ name: 'Aurora Dev.', origin: 'Referral', value: '$220k', when: 'today' }],
        title: 'Won',
        tone: '#7bb86b',
      },
    ],
    eyebrow: 'CRM',
    filterLabel: 'Filter',
    heading: 'The new inquiry stops slipping through the cracks',
    intro:
      'A pipeline with source, estimated value, and follow-up. A lead becomes a contact, the contact becomes a brief, the brief becomes a proposal, the proposal becomes a project — without leaving this page.',
    mockupTitle: 'Client pipeline',
    newLeadLabel: '+ New lead',
    origins: [
      { count: 18, label: 'Website', share: 32, tone: '#5b8def' },
      { count: 14, label: 'Referral', share: 25, tone: '#7bb86b' },
      { count: 11, label: 'Instagram', share: 20, tone: '#d96fa3' },
      { count: 8, label: 'Brief', share: 14, tone: '#e8a13d' },
      { count: 5, label: 'Ads', share: 9, tone: '#7a6cb6' },
    ],
    originsCaption: 'Where clients are coming from',
    originsPeriod: 'Last 90 days',
    originsTotal: '56 leads',
    stats: [
      { label: 'In pipeline', tone: '#2a5dc1', value: '$463k' },
      { label: 'Win rate, 30d', tone: '#7bb86b', value: '38%' },
      { label: 'Follow-ups due', tone: '#e8a13d', value: '4' },
      { label: 'Average time to close', tone: '#7a6cb6', value: '12 days' },
    ],
    trendBadge: '▲ 12% vs. Apr',
  },
  'pt-BR': {
    columns: [
      {
        cards: [
          { name: 'Mariana Vieira', origin: 'Briefing site', value: '~R$ 80k', when: 'hoje' },
          { name: 'Pedro Salles', origin: 'Indicação', value: '~R$ 45k', when: 'ontem' },
        ],
        title: 'Novo lead',
        tone: '#5b8def',
      },
      {
        cards: [{ name: 'Studio Carmim', origin: 'Instagram', value: '~R$ 32k', when: '3 dias' }],
        title: 'Em contato',
        tone: '#e8a13d',
      },
      {
        cards: [
          { name: 'Família Souto', origin: 'Cliente recorrente', value: 'R$ 86k', when: '5 dias' },
        ],
        title: 'Proposta enviada',
        tone: '#7a6cb6',
      },
      {
        cards: [{ name: 'Aurora Inc.', origin: 'Indicação', value: 'R$ 220k', when: 'hoje' }],
        title: 'Vira cliente',
        tone: '#7bb86b',
      },
    ],
    eyebrow: 'CRM',
    filterLabel: 'Filtrar',
    heading: 'O cliente novo deixa de cair no esquecimento',
    intro:
      'Pipeline com origem, valor estimado e follow-up. Lead vira contato, contato vira briefing, briefing vira proposta, proposta vira projeto, sem sair daqui.',
    mockupTitle: 'Pipeline de clientes',
    newLeadLabel: '+ Novo lead',
    origins: [
      { count: 18, label: 'Site', share: 32, tone: '#5b8def' },
      { count: 14, label: 'Indicação', share: 25, tone: '#7bb86b' },
      { count: 11, label: 'Instagram', share: 20, tone: '#d96fa3' },
      { count: 8, label: 'Briefing', share: 14, tone: '#e8a13d' },
      { count: 5, label: 'Anúncio', share: 9, tone: '#7a6cb6' },
    ],
    originsCaption: 'De onde os clientes estão vindo',
    originsPeriod: 'Últimos 90 dias',
    originsTotal: '56 leads',
    stats: [
      { label: 'Em pipeline', tone: '#2a5dc1', value: 'R$ 463k' },
      { label: 'Conversão 30d', tone: '#7bb86b', value: '38%' },
      { label: 'Follow-ups pendentes', tone: '#e8a13d', value: '4' },
      { label: 'Tempo médio de fechamento', tone: '#7a6cb6', value: '12 dias' },
    ],
    trendBadge: '▲ 12% vs. abr',
  },
}

const CrmMockup = ({ content }: { content: CrmContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_24px_60px_-26px_rgba(40,40,80,0.28)]">
    <div className="flex items-center justify-between border-[#f1f1f1] border-b px-5 py-3">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-[#1c1c20] text-[13px]">{content.mockupTitle}</span>
        <span className="rounded-md bg-[#dfeedb] px-2 py-1 font-medium text-[#3e7d33] text-[10px]">
          {content.trendBadge}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="rounded-md border border-[#ececec] px-2 py-1 text-[#6c6c72] text-[10.5px]">
          {content.filterLabel}
        </span>
        <span className="rounded-md bg-[#1d1d20] px-2 py-1 text-[10.5px] text-white">
          {content.newLeadLabel}
        </span>
      </div>
    </div>
    <div className="grid grid-cols-4 gap-2 p-3">
      {content.columns.map((col) => (
        <div className="rounded-xl bg-[#fafafa] p-2.5" key={col.title}>
          <div className="flex items-center gap-1.5 px-1 pb-2">
            <span className="size-[7px] rounded-full" style={{ background: col.tone }} />
            <span className="font-semibold text-[#1c1c20] text-[11px]">{col.title}</span>
            <span className="ml-auto text-[#9a9aa0] text-[10px]">{col.cards.length}</span>
          </div>
          <div className="space-y-2">
            {col.cards.map((card) => (
              <div className="rounded-lg border border-[#ececec] bg-white p-2.5" key={card.name}>
                <p className="font-medium text-[#1c1c20] text-[11.5px] leading-snug">{card.name}</p>
                <p className="mt-0.5 text-[#9a9aa0] text-[10px]">{card.origin}</p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-semibold text-[#43434a] text-[10.5px]">{card.value}</span>
                  <span className="text-[#a8a8ad] text-[9.5px]">{card.when}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
)

export const CrmSection = ({
  headingLevel: Heading = 'h2',
}: {
  /** `h1` when the section opens a deep-dive page and owns its top heading. */
  headingLevel?: 'h1' | 'h2'
}) => {
  const content = useLocalized(CONTENT)

  return (
    <section className="relative overflow-hidden bg-[#f3f6f8] py-28">
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="crm"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <p className="text-[#2a5dc1] text-[12px] tracking-[0.2em]">{content.eyebrow}</p>
          <Heading className="mx-auto mt-3 max-w-[820px] font-display text-[#0b0b0d] text-[clamp(40px,6vw,58px)] leading-[1.06] tracking-[-0.01em]">
            {content.heading}
          </Heading>
          <p className="mx-auto mt-5 max-w-[640px] text-[#6c6c72] text-[clamp(16px,2.2vw,20px)]">
            {content.intro}
          </p>
        </ScrollReveal>

        {/* Horizontal stat strip, sets up the kanban */}
        <ScrollReveal className="mt-14">
          <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-[#dfe5ea] bg-white sm:grid-cols-4">
            {content.stats.map((stat, i) => (
              <div
                className={`px-5 py-5 ${i > 0 ? 'border-[#dfe5ea] border-t sm:border-t-0 sm:border-l' : ''}`}
                key={stat.label}
              >
                <p className="text-[#6e7780] text-[11.5px]">{stat.label}</p>
                <p className="mt-1 font-bold text-[#0b0b0d] text-[22px]">{stat.value}</p>
                <span
                  className="mt-1.5 inline-block size-[6px] rounded-full"
                  style={{ background: stat.tone }}
                />
              </div>
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal className="mt-6" delay={100}>
          <CrmMockup content={content} />
        </ScrollReveal>

        {/* Origins band, a single horizontal chart, not three cards */}
        <ScrollReveal className="mt-10" delay={150}>
          <div className="rounded-2xl border border-[#dfe5ea] bg-white p-6">
            <div className="flex items-baseline justify-between">
              <div>
                <p className="text-[#6e7780] text-[12px]">{content.originsCaption}</p>
                <p className="font-bold text-[#0b0b0d] text-[18px]">{content.originsPeriod}</p>
              </div>
              <span className="text-[#6e7780] text-[12px]">{content.originsTotal}</span>
            </div>
            <div className="mt-5 flex h-[16px] overflow-hidden rounded-full">
              {content.origins.map((o) => (
                <span
                  className="h-full"
                  key={o.label}
                  style={{ background: o.tone, width: `${o.share}%` }}
                />
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-5">
              {content.origins.map((o) => (
                <div className="flex items-center gap-2" key={o.label}>
                  <span className="size-[10px] rounded-full" style={{ background: o.tone }} />
                  <span className="text-[#43434a] text-[12.5px]">{o.label}</span>
                  <span className="ml-auto font-medium text-[#0b0b0d] text-[12px]">{o.count}</span>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  )
}
